import './SearchInput.scss';

import React, { useEffect, useRef } from 'react';
import {
	getPresetInputValue,
	isViUpdate,
	submitTermSearch,
} from '../../helpers';

import PropTypes from 'prop-types';
import cx from 'classnames';
import tagging from '../../helpers/tagging';
import { useStateValue } from '../../state';

const SearchInput = React.forwardRef(
	(
		{
			getInputProps,
			getComboboxProps,
			setInputValue,
			openCbMenu,
			cbReset,
			enableButtons,
			searchResults,
			isOpen,
		},
		ref
	) => {
		const [
			{
				searchParamKey,
				_t,
				clientCode,
				isPopularSearchesEnabled,
				inputFocused,
			},
			dispatch,
		] = useStateValue();

		const [state] = useStateValue();
		const searchBtnRef = useRef(null);

		const handleInputFocus = () => {
			const value = ref.current.value;
			dispatch({ type: 'setInputFocused', inputFocused: true });

			if (value.length) {
				openCbMenu();
			}

			if (isPopularSearchesEnabled) {
				openCbMenu();
				dispatch({ type: 'showPopularSearches', showPopularSearches: true });
			}
		};

		const handleClearInput = () => {
			setInputValue('');

			cbReset();

			// onFocus triggers before setInputValue so force local value
			ref.current.value = '';
			ref.current.focus();
		};

		const handleSubmitSearch = () => {
			if (!ref.current.value) return;

			let suggestedProducts;

			if (searchResults && searchResults.length) {
				suggestedProducts = searchResults.filter(
					(result) => result.PredictiveSearchType === 'Product'
				);
			}

			tagging.initTagging({ ...state, suggestedProducts });

			submitTermSearch(clientCode, ref.current.value);
		};

		useEffect(() => {
			const presetInputValue = getPresetInputValue();

			if (presetInputValue) {
				setInputValue(presetInputValue);
			}
		}, []);

		const inputProps = getInputProps({
			ref,
		});

		const hasResults = isOpen && searchResults && searchResults.length;
		const borderColor =
			isOpen || inputFocused ? 'border-cta-main' : 'border-ongreen';

		const currentSearchTerm = ref?.current?.value ?? '';
		return (
			<div
				data-tag-section="sitesearch"
				data-testid="siteSearch-form"
				className={cx(
					'siteSearch__input-container',
					'box-border',
					'overflow-hidden',
					'relative',
					'flex',
					'h-10',
					'flex-1'
				)}
				{...getComboboxProps()}>
				<input
					className={cx(
						'siteSearch__input box-border c-input-text u-w--100 text-body h-10 px-4 py-0 bg-transparent',
						'border border-1 border-r-0',
						borderColor,
						!hasResults ? 'rounded-l-full' : 'rounded-tl-xl'
					)}
					id="siteSearchInput"
					{...inputProps}
					onFocus={handleInputFocus}
					onBlur={(e) => {
						dispatch({ type: 'setInputFocused', inputFocused: false });
						inputProps.onBlur(e);
					}}
					onKeyDown={(e) => {
						if(e.key === "Enter") {
							searchBtnRef.current.click();
						}
						inputProps.onKeyDown(e)
					}}
					data-testid="siteSearch-input"
					type="search"
					name={searchParamKey}
					placeholder={_t.placeholder}
				/>

				{enableButtons ? (
					<button
						type="button"
						aria-label="Clear search input"
						data-testid="siteSearch-clear-button"
						className={cx(
							'siteSearch__clear-button box-border p-0 text-tertiary mr-0 h-10 bg-transparent flex flex-col items-center justify-center',
							'border border-r-0 border-l-0 border-t-1 border-b-1',
							borderColor
						)}
						onClick={handleClearInput}>
						{!isViUpdate() && <span>×</span>}
						{isViUpdate() && (
							<span className="ico ico-close text-primary text-lg leading-none"></span>
						)}
					</button>
				) : null}

				<button
					data-tag-name="submit search"
					data-tag-searchterm={currentSearchTerm}
					type="button"
					aria-label="Submit search"
					onClick={handleSubmitSearch}
					data-testid="siteSearch-submit-button"
					ref={searchBtnRef}
					className={cx(
						'siteSearch__search-button',
						'box-border',
						'flex items-center justify-center',
						'border-1',
						'p-0 m-0',
						'before-hidden',
						'text-white',
						'focus:bg-cta-main focus:border-cta-main hover:bg-cta-main hover:border-cta-main',
						'icon-',
						'static',
						isOpen || inputFocused ? 'bg-cta-main' : 'bg-ongreen',
						borderColor,
						!hasResults ? 'rounded-r-full' : 'rounded-tr-xl'
					)}>
					{isViUpdate() && (
						<span className="ico ico-search leading-none static" />
					)}
				</button>
			</div>
		);
	}
);

SearchInput.propTypes = {
	getComboboxProps: PropTypes.func,
	getInputProps: PropTypes.func,
	setInputValue: PropTypes.func,
	openCbMenu: PropTypes.func,
	cbReset: PropTypes.func,
	enableButtons: PropTypes.bool,
	searchResults: PropTypes.array,
	isOpen: PropTypes.bool,
};

export default SearchInput;
